import { useEffect, useMemo, useState } from 'react';
import { Pressable, ScrollView, Text, useWindowDimensions, View } from 'react-native';
import { trpc } from '../lib/trpc';
import { Badge } from './Badge';
import { BottomSheet } from './BottomSheet';
import { BrandSwitch } from './BrandSwitch';
import { Button } from './Button';
import { CategoryPickerModal } from './CategoryPickerModal';
import { FormErrorBanner } from './FormErrorBanner';
import { LoadingSpinner } from './LoadingSpinner';
import { Money } from './Money';
import { Select } from './Select';
import { TextInput } from './TextInput';
import type { Transaction } from './TransactionList';
import { webPrivacyProps } from '../lib/privacy';
import { formatCurrency, formatDate } from '../lib/utils';
import { useIsDesktop } from '../hooks/useIsDesktop';

type RuleStep = 'criteria' | 'review' | 'category';

interface CategoryRuleBuilderProps {
  visible: boolean;
  onClose: () => void;
  onCreated?: () => void | Promise<void>;
}

function parseAmount(value: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const amount = Number(trimmed);
  return Number.isFinite(amount) ? amount : Number.NaN;
}

export function CategoryRuleBuilder({ visible, onClose, onCreated }: CategoryRuleBuilderProps) {
  const { height: windowHeight } = useWindowDimensions();
  const isDesktop = useIsDesktop();
  const utils = trpc.useUtils();
  const [step, setStep] = useState<RuleStep>('criteria');
  const [merchantName, setMerchantName] = useState('');
  const [nameContains, setNameContains] = useState('');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [accountId, setAccountId] = useState<string | null>(null);
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);
  const [applyRetroactively, setApplyRetroactively] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewRequested, setPreviewRequested] = useState(false);

  const minAmount = parseAmount(amountMin);
  const maxAmount = parseAmount(amountMax);
  const trimmedMerchantName = merchantName.trim();
  const trimmedNameContains = nameContains.trim();

  const ruleCriteria = useMemo(
    () => ({
      ...(trimmedMerchantName ? { merchantName: trimmedMerchantName } : {}),
      ...(trimmedNameContains ? { nameContains: trimmedNameContains } : {}),
      ...(minAmount !== undefined && Number.isFinite(minAmount) ? { amountMin: minAmount } : {}),
      ...(maxAmount !== undefined && Number.isFinite(maxAmount) ? { amountMax: maxAmount } : {}),
      ...(accountId ? { accountId } : {}),
    }),
    [accountId, maxAmount, minAmount, trimmedMerchantName, trimmedNameContains],
  );
  const previewCriteria = useMemo(
    () => ({
      ...(trimmedMerchantName ? { merchantName: trimmedMerchantName } : {}),
      ...(trimmedNameContains ? { search: trimmedNameContains } : {}),
      ...(minAmount !== undefined && Number.isFinite(minAmount) ? { amountMin: minAmount } : {}),
      ...(maxAmount !== undefined && Number.isFinite(maxAmount) ? { amountMax: maxAmount } : {}),
      ...(accountId ? { accountId } : {}),
    }),
    [accountId, maxAmount, minAmount, trimmedMerchantName, trimmedNameContains],
  );
  const hasCriteria = Object.keys(ruleCriteria).length > 0;
  const hasAmountError =
    (minAmount !== undefined && !Number.isFinite(minAmount)) ||
    (maxAmount !== undefined && !Number.isFinite(maxAmount));

  const categoriesQ = trpc.budget.listCategories.useQuery(undefined, { enabled: visible });
  const accountsQ = trpc.accounts.list.useQuery(undefined, { enabled: visible });
  const previewQ = trpc.transactions.list.useQuery(
    {
      page: 1,
      pageSize: 100,
      sortBy: 'date',
      sortOrder: 'desc',
      ...previewCriteria,
    },
    {
      enabled: visible && previewRequested && hasCriteria && !hasAmountError,
    },
  );
  const createRuleMutation = trpc.categoryRules.create.useMutation();

  const accountOptions = (accountsQ.data ?? []).map((account) => ({
    value: account.id,
    label: account.name,
  }));
  const previewTransactions = (previewQ.data?.items ?? []) as Transaction[];
  const previewTotal = previewQ.data?.total ?? 0;
  const selectedCategory = (categoriesQ.data ?? []).find((category) => category.id === categoryId);
  const modalHeight = Math.min(windowHeight * (isDesktop ? 0.78 : 0.82), isDesktop ? 760 : 720);

  useEffect(() => {
    if (visible) return;
    setStep('criteria');
    setMerchantName('');
    setNameContains('');
    setAmountMin('');
    setAmountMax('');
    setAccountId(null);
    setCategoryId(null);
    setCategoryPickerOpen(false);
    setApplyRetroactively(true);
    setError(null);
    setPreviewRequested(false);
  }, [visible]);

  const handleFindMatches = () => {
    setError(null);
    if (!hasCriteria) {
      setError('Add at least one matching condition.');
      return;
    }
    if (hasAmountError) {
      setError('Enter valid dollar amounts, or leave the amount fields blank.');
      return;
    }
    setPreviewRequested(true);
    setStep('review');
  };

  const handleCreateRule = async () => {
    setError(null);
    if (!categoryId) {
      setError('Choose the category matching transactions should use.');
      return;
    }

    await createRuleMutation.mutateAsync({
      newCategoryId: categoryId,
      ...ruleCriteria,
      applyRetroactively,
    });
    await Promise.all([
      utils.categoryRules.list.invalidate(),
      utils.transactions.list.invalidate(),
      utils.transactions.reviewCount.invalidate(),
    ]);
    await onCreated?.();
    onClose();
  };

  const stepPills = (
    <View className="flex-row gap-2">
      {(['criteria', 'review', 'category'] as RuleStep[]).map((item, index) => {
        const active = step === item;
        const label = index === 0 ? 'Find' : index === 1 ? 'Confirm' : 'Apply';
        return (
          <View
            key={item}
            className={`flex-1 rounded-full px-3 py-1.5 ${
              active ? 'bg-forest-600' : 'bg-gray-100 dark:bg-gray-700'
            }`}
          >
            <Text
              className={`text-xs text-center font-semibold ${
                active ? 'text-white' : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {label}
            </Text>
          </View>
        );
      })}
    </View>
  );

  const currentError = error ?? createRuleMutation.error?.message ?? null;

  const footer =
    step === 'criteria' ? (
      <Button
        testID="category-rule-find-matches-button"
        accessibilityLabel="Find matching transactions for rule"
        onPress={handleFindMatches}
      >
        Find matching transactions
      </Button>
    ) : step === 'review' ? (
      <View className="flex-row gap-3">
        <Button
          testID="category-rule-edit-criteria-button"
          accessibilityLabel="Edit rule criteria"
          variant="secondary"
          className="flex-1"
          onPress={() => setStep('criteria')}
        >
          Edit criteria
        </Button>
        <Button
          testID="category-rule-review-confirm-button"
          accessibilityLabel="Confirm rule matches"
          className="flex-1"
          onPress={() => setStep('category')}
          disabled={previewTransactions.length === 0}
        >
          These are right
        </Button>
      </View>
    ) : (
      <View className="flex-row gap-3">
        <Button
          testID="category-rule-back-button"
          accessibilityLabel="Back to rule matches"
          variant="secondary"
          className="flex-1"
          onPress={() => setStep('review')}
        >
          Back
        </Button>
        <Button
          testID="category-rule-create-button"
          accessibilityLabel="Create category rule"
          className="flex-1"
          onPress={handleCreateRule}
          loading={createRuleMutation.isPending}
        >
          Create Rule
        </Button>
      </View>
    );

  return (
    <BottomSheet
      visible={visible}
      onClose={onClose}
      title="Create Rule"
      variant={isDesktop ? 'centered' : 'bottom'}
      contentStyle={{ height: modalHeight }}
    >
      <View className="flex-1">
        <View className="px-5 pt-5 pb-3 gap-4 border-b border-gray-100 dark:border-gray-700">
          {stepPills}
          <FormErrorBanner error={currentError} />
        </View>

        {step === 'criteria' && (
          <ScrollView
            className="flex-1 px-5 py-4"
            keyboardShouldPersistTaps="handled"
            nestedScrollEnabled
          >
            <View className="gap-4 pb-2">
              <TextInput
                testID="category-rule-merchant-input"
                accessibilityLabel="Merchant contains"
                label="Merchant contains"
                placeholder="e.g. Starbucks"
                value={merchantName}
                onChangeText={setMerchantName}
                autoCapitalize="words"
              />
              <TextInput
                testID="category-rule-description-input"
                accessibilityLabel="Description contains"
                label="Description contains"
                placeholder="e.g. payroll, uber, card ending"
                value={nameContains}
                onChangeText={setNameContains}
                autoCapitalize="none"
              />
              <View className="flex-row gap-3">
                <TextInput
                  testID="category-rule-min-amount-input"
                  accessibilityLabel="Minimum amount"
                  label="Min amount"
                  prefix="$"
                  placeholder="0"
                  keyboardType="decimal-pad"
                  value={amountMin}
                  onChangeText={setAmountMin}
                  containerClassName="flex-1"
                />
                <TextInput
                  testID="category-rule-max-amount-input"
                  accessibilityLabel="Maximum amount"
                  label="Max amount"
                  prefix="$"
                  placeholder="500"
                  keyboardType="decimal-pad"
                  value={amountMax}
                  onChangeText={setAmountMax}
                  containerClassName="flex-1"
                />
              </View>
              <Select
                label="Account"
                value={accountId}
                options={accountOptions}
                onChange={setAccountId}
                placeholder="Any account"
              />
            </View>
          </ScrollView>
        )}

        {step === 'review' && (
          <View className="flex-1">
            <ScrollView
              className="flex-1 px-5 py-4"
              nestedScrollEnabled
            >
              {previewQ.isLoading ? (
                <LoadingSpinner fullScreen={false} message="Finding matches…" />
              ) : previewTransactions.length === 0 ? (
                <View className="items-center rounded-xl bg-gray-50 dark:bg-gray-900 px-4 py-6">
                  <Text className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    No matches found
                  </Text>
                  <Text className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-center">
                    Adjust the criteria before creating this rule.
                  </Text>
                </View>
              ) : (
                <View className="rounded-xl overflow-hidden border border-gray-100 dark:border-gray-700">
                  <View className="px-4 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-100 dark:border-gray-700">
                    <Text className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                      {previewTotal > previewTransactions.length
                        ? `Showing ${previewTransactions.length} of ${previewTotal} matches`
                        : `${previewTotal} matching transactions`}
                    </Text>
                  </View>
                  {previewTransactions.map((transaction) => (
                    <View
                      key={transaction.id}
                      className="flex-row items-center justify-between gap-3 px-4 py-3 border-b border-gray-50 dark:border-gray-700"
                    >
                      <View className="flex-1">
                        <Text
                          className="text-sm font-semibold text-gray-800 dark:text-gray-200"
                          numberOfLines={1}
                          {...webPrivacyProps('private')}
                        >
                          {transaction.name}
                        </Text>
                        <View className="flex-row items-center gap-2 mt-1">
                          <Text className="text-xs text-gray-400 dark:text-gray-500">
                            {formatDate(transaction.date, 'short')}
                          </Text>
                          <Badge label={transaction.categoryName ?? 'Uncategorized'} variant="gray" />
                        </View>
                      </View>
                      <Money className="text-sm text-gray-800 dark:text-gray-200" weight={500}>
                        {transaction.amount < 0 ? '+' : ''}
                        {formatCurrency(Math.abs(transaction.amount))}
                      </Money>
                    </View>
                  ))}
                </View>
              )}
            </ScrollView>
          </View>
        )}

        {step === 'category' && (
          <ScrollView
            className="flex-1 px-5 py-4"
            keyboardShouldPersistTaps="handled"
            nestedScrollEnabled
          >
            <View className="gap-4 pb-2">
              <View className="rounded-xl bg-gray-50 dark:bg-gray-900 px-4 py-3">
                <Text className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                  Confirmed matches
                </Text>
                <Text className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                  {previewTotal > 0
                    ? `${previewTotal} existing transaction${previewTotal === 1 ? '' : 's'} match this rule.`
                    : 'This rule will only apply to future transactions unless matches appear later.'}
                </Text>
              </View>
              <View>
                <Text className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  New category
                </Text>
                <Pressable
                  testID="category-rule-category-picker-button"
                  accessibilityLabel="Choose rule category"
                  onPress={() => setCategoryPickerOpen(true)}
                  className="min-h-[48px] flex-row items-center justify-between rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-3"
                >
                  <Text
                    className={`text-base ${
                      selectedCategory
                        ? 'text-gray-900 dark:text-gray-100'
                        : 'text-gray-400 dark:text-gray-500'
                    }`}
                  >
                    {selectedCategory?.name ?? 'Choose category'}
                  </Text>
                  <Text className="text-gray-400 dark:text-gray-500">›</Text>
                </Pressable>
              </View>
              <View className="flex-row items-center justify-between gap-4 rounded-xl bg-gray-50 dark:bg-gray-900 px-4 py-3">
                <View className="flex-1">
                  <Text className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                    Apply to existing matches
                  </Text>
                  <Text className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {applyRetroactively
                      ? `Update the matching transactions${selectedCategory ? ` to ${selectedCategory.name}` : ''}.`
                      : 'Only categorize future transactions that match.'}
                  </Text>
                </View>
                <BrandSwitch
                  testID="category-rule-apply-existing-switch"
                  value={applyRetroactively}
                  onValueChange={setApplyRetroactively}
                  accessibilityLabel="Apply rule to existing matches"
                />
              </View>
            </View>
          </ScrollView>
        )}

        <View className="px-5 py-4 border-t border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
          {footer}
        </View>
      </View>
      <CategoryPickerModal
        visible={categoryPickerOpen}
        onClose={() => setCategoryPickerOpen(false)}
        selectedCategoryId={categoryId}
        onSelect={(id) => {
          setCategoryId(id);
          setCategoryPickerOpen(false);
        }}
        title="New category"
        subtitle="Search or browse budget categories"
      />
    </BottomSheet>
  );
}
